// SkipNav — sidebar nav for the Skip Management module.
//
// Per SKIP_MODULE_PLAN.md §5. Modelled on CollectionsNav: same item
// structure (NavItem + NavSection), same role-gating, reuses the
// CollectionsNav.module.css so the visual output is identical between
// modules.
//
// Item set matches the legacy `tricivo_system/operations/skips/`
// labels: Dashboard / Jobs / Enquiries / Inventory / Customers. An
// admin-only Reports stub is included for forward-compat (the route
// renders <ComingSoon /> until the reports module ships).
//
// Workflow actions (collection-request / exchange-request / tip-return-
// request) are NOT top-level nav items — they live as per-row action
// buttons on the jobs list and detail pages (§7.12 — chooser dropped).
//
// Notes:
//   - Inventory is staff-visible per §7.11 (Add/Edit/Adjust = staff,
//     Delete = admin). The nav exposes the listing to all staff; the
//     create / edit pages enforce role gates at the route layer.

import { type ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Boxes,
  Container,
  Inbox,
  LayoutDashboard,
  ListChecks,
  Map,
  PlusCircle,
  Truck,
  Users,
  Workflow,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useAuthStore } from '../../../store/authStore';
import { useSidebar } from '../../../components/shell/SidebarContext';
import type { UserSummary } from '../../../types/api';
import { useSkipEnquiries } from '../hooks/useSkipEnquiries';
import { useSkipPendingRequestsCount } from '../hooks/useSkipPendingRequestsCount';
import styles from '../../collections/components/CollectionsNav.module.css';

type Role = UserSummary['role'];

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
  roles?: Role[];
  end?: boolean;
  /** When set, this key resolves to a numeric badge rendered next to
   *  the label. The mapping lives in SkipNav (see badge resolution
   *  below). Used today by Enquiries → open count. */
  badgeKey?: 'open_enquiries';
}

interface NavSection {
  label?: string;
  roles?: Role[];
  items: NavItem[];
}

const STAFF_ROLES: Role[] = ['staff', 'admin'];

const SECTIONS: NavSection[] = [
  {
    items: [
      { to: '/skip/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true, roles: STAFF_ROLES },
      { to: '/skip/jobs', label: 'Jobs', icon: Truck, roles: STAFF_ROLES },
      {
        to: '/skip/enquiries',
        label: 'Enquiries',
        icon: Inbox,
        roles: STAFF_ROLES,
        badgeKey: 'open_enquiries',
      },
      { to: '/skip/customers', label: 'Customers', icon: Users, roles: STAFF_ROLES },
      { to: '/skip/drivers/map', label: 'Driver Map', icon: Map, roles: STAFF_ROLES },
    ],
  },
  {
    label: 'Create',
    roles: STAFF_ROLES,
    items: [
      {
        to: '/skip/jobs/new',
        label: 'Create / Update',
        icon: PlusCircle,
        roles: STAFF_ROLES,
      },
      {
        to: '/skip/jobs/active',
        label: 'Active Jobs',
        icon: ListChecks,
        roles: STAFF_ROLES,
      },
    ],
  },
  {
    label: 'Settings',
    roles: STAFF_ROLES,
    items: [
      { to: '/skip/inventory', label: 'Inventory', icon: Container, roles: STAFF_ROLES },
      {
        to: '/skip/settings/stock-types',
        label: 'Stock Types',
        icon: Boxes,
        roles: STAFF_ROLES,
      },
      {
        to: '/skip/settings/job-types',
        label: 'Job Types',
        icon: Workflow,
        roles: STAFF_ROLES,
      },
    ],
  },
];

export function SkipNav(): JSX.Element | null {
  const role = useAuthStore((s) => s.user?.role);
  const { isCollapsed } = useSidebar();

  // Badge data — keep the requests small (limit=1) since we only need
  // pagination.total. The list view's own queries use different keys
  // but they don't conflict.
  const openEnquiriesQuery = useSkipEnquiries({ status: 'open', limit: 1 });
  const openCount = openEnquiriesQuery.data?.pagination.total ?? 0;
  const pendingRequestsQuery = useSkipPendingRequestsCount();
  const pendingCount = pendingRequestsQuery.data ?? 0;

  function visible(roles?: Role[]): boolean {
    if (!roles) return true;
    if (!role) return false;
    return roles.includes(role);
  }

  function badgeFor(key?: NavItem['badgeKey']): number | null {
    if (key === 'open_enquiries') {
      const total = openCount + pendingCount;
      return total > 0 ? total : null;
    }
    return null;
  }

  return (
    <div className={cn(styles.nav, isCollapsed && styles.collapsed)}>
      {SECTIONS.map((section, idx) => {
        if (!visible(section.roles)) return null;
        const items = section.items.filter((it) => visible(it.roles));
        if (items.length === 0) return null;
        return (
          <div key={idx} className={styles.section}>
            {section.label ? (
              <span className={styles.sectionLabel}>{section.label}</span>
            ) : null}
            <ul className={styles.list}>
              {items.map((item) => {
                const badge = badgeFor(item.badgeKey);
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) => cn(styles.link, isActive && styles.linkActive)}
                      title={isCollapsed ? item.label : undefined}
                    >
                      <span className={styles.linkInner}>
                        <span className={styles.icon}>
                          <item.icon size={18} aria-hidden />
                        </span>
                        <span className={styles.label}>{item.label}</span>
                        {badge !== null ? (
                          <span className={styles.badge} aria-label={`${badge} open`}>
                            {badge}
                          </span>
                        ) : null}
                      </span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
